import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { Toaster } from 'sonner';
import { Plus, Pencil, Sparkles, Megaphone } from 'lucide-react';
import { getAutoClients } from '../utils/api';
import { ClientModal } from '../components/automation/ClientModal';
import { GenerateModal } from '../components/automation/GenerateModal';
import { ApprovalQueue } from '../components/automation/ApprovalQueue';
import { AutomationDashboard } from '../components/automation/AutomationDashboard';
import { ContentCalendar } from '../components/automation/ContentCalendar';
import { AdCampaigns } from '../components/automation/AdCampaigns';

const TABS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'queue', label: 'Approval Queue' },
  { id: 'calendar', label: 'Calendar' },
  { id: 'ads', label: 'Ad Campaigns' },
];

export default function Automation() {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [tab, setTab] = useState('dashboard');
  const [showClientModal, setShowClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);

  const bump = () => setRefreshKey((k) => k + 1);

  const fetchClients = useCallback(async (selectId) => {
    try {
      const res = await getAutoClients();
      setClients(res.data.clients);
      if (selectId) {
        setSelectedClientId(selectId);
      } else if (!selectedClientId && res.data.clients.length > 0) {
        setSelectedClientId(res.data.clients[0].id);
      }
    } catch (err) {
      console.error('Failed to load clients', err);
    } finally {
      setLoading(false);
    }
  }, [selectedClientId]);

  useEffect(() => { fetchClients(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedClient = clients.find((c) => c.id === selectedClientId);

  return (
    <Layout variant="light">
      <Toaster theme="dark" position="top-right" richColors />
      <div data-testid="automation-page">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
          <div>
            <h1 className="text-3xl font-black text-white flex items-center gap-3">
              <Megaphone className="text-lime" size={28} /> Marketing Automation
            </h1>
            <p className="text-gray mt-1">AI drafts → you approve → auto-publish to social & ads. HVAC, Plumbing & Electrical clients.</p>
          </div>
          <div className="flex items-center gap-3">
            {clients.length > 0 && (
              <select
                data-testid="automation-client-select"
                className="bg-card-bg border border-divider rounded-lg px-3 py-2 text-white text-sm focus:border-lime focus:outline-none"
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
              >
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.trade ? ` (${c.trade})` : ''}</option>)}
              </select>
            )}
            {selectedClient && (
              <button
                data-testid="edit-client-btn"
                onClick={() => { setEditingClient(selectedClient); setShowClientModal(true); }}
                className="p-2 rounded-lg border border-divider text-gray-light hover:text-white"
                title="Edit client"
              >
                <Pencil size={16} />
              </button>
            )}
            <button
              data-testid="add-client-btn"
              onClick={() => { setEditingClient(null); setShowClientModal(true); }}
              className="px-4 py-2 rounded-lg border border-lime text-lime font-bold text-sm flex items-center gap-2 hover:bg-lime/10"
            >
              <Plus size={16} /> New Client
            </button>
            {clients.length > 0 && (
              <button
                data-testid="generate-content-btn"
                onClick={() => setShowGenerate(true)}
                className="px-4 py-2 rounded-lg bg-lime text-black font-bold text-sm flex items-center gap-2 hover:bg-lime/90"
              >
                <Sparkles size={16} /> Generate Content
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-gray py-16 text-center">Loading…</div>
        ) : clients.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-divider rounded-xl mt-8" data-testid="automation-empty-state">
            <Megaphone className="mx-auto text-gray mb-4" size={40} />
            <h3 className="text-white font-bold text-xl mb-2">Add your first client</h3>
            <p className="text-gray mb-6">Set up their business profile — HVAC, Plumbing or Electrical — so AI can draft on-brand posts and ads.</p>
            <button
              data-testid="empty-add-client-btn"
              onClick={() => { setEditingClient(null); setShowClientModal(true); }}
              className="px-6 py-3 rounded-lg bg-lime text-black font-bold hover:bg-lime/90"
            >
              + Add Client
            </button>
          </div>
        ) : (
          <>
            <div className="flex gap-2 mt-6 mb-6 border-b border-divider">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  data-testid={`tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                    tab === t.id ? 'border-lime text-lime' : 'border-transparent text-gray hover:text-white'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {selectedClientId && (
              <>
                {tab === 'dashboard' && <AutomationDashboard clientId={selectedClientId} refreshKey={refreshKey} />}
                {tab === 'queue' && <ApprovalQueue clientId={selectedClientId} refreshKey={refreshKey} onChanged={bump} />}
                {tab === 'calendar' && <ContentCalendar clientId={selectedClientId} refreshKey={refreshKey} />}
                {tab === 'ads' && <AdCampaigns clientId={selectedClientId} refreshKey={refreshKey} onChanged={bump} />}
              </>
            )}
          </>
        )}
      </div>

      {showClientModal && (
        <ClientModal
          client={editingClient}
          onClose={() => setShowClientModal(false)}
          onSaved={(saved) => { setShowClientModal(false); fetchClients(saved?.id); }}
        />
      )}
      {showGenerate && (
        <GenerateModal
          clients={clients}
          defaultClientId={selectedClientId}
          onClose={() => setShowGenerate(false)}
          onGenerated={() => { setShowGenerate(false); setTab('queue'); bump(); }}
        />
      )}
    </Layout>
  );
}
